import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { hasPermission } from '../../utils/permissions';
import { selectPermissions } from '../../pages/Login/slice';
import config from '../../config';
import './Sidebar.css';

function Sidebar() {
    const { t } = useTranslation();
    const location = useLocation();
    // Subscribe to permissions from Redux state to trigger re-render when permissions are loaded
    const reduxPermissions = useSelector(selectPermissions);

    const [expandedModule, setExpandedModule] = useState(null);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [hoveredModule, setHoveredModule] = useState(null);
    const [popupPosition, setPopupPosition] = useState({ top: 0 });
    const hideTimeoutRef = useState(null);

    const toggleModule = (moduleNumber) => {
        if (!isCollapsed) {
            setExpandedModule(expandedModule === moduleNumber ? null : moduleNumber);
        }
    };

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const handleModuleMouseEnter = (moduleId, event) => {
        if (isCollapsed) {
            // Clear any pending hide timeout
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
                hideTimeoutRef.current = null;
            }
            const rect = event.currentTarget.getBoundingClientRect();
            setPopupPosition({ top: rect.top });
            setHoveredModule(moduleId);
        }
    };

    const handleModuleMouseLeave = () => {
        if (isCollapsed) {
            // Delay hiding to allow mouse to move to popup
            hideTimeoutRef.current = setTimeout(() => {
                setHoveredModule(null);
            }, 300);
        }
    };

    const handlePopupMouseEnter = () => {
        // Clear hide timeout when mouse enters popup
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    };

    const handlePopupMouseLeave = () => {
        if (isCollapsed) {
            setHoveredModule(null);
        }
    };

    const modules = [
        {
            id: 1,
            titleKey: 'sidebar.module1.title',
            icon: 'fas fa-laptop',
            color: 'module-1',
            submenu: [
                {
                    path: '/devices',
                    icon: 'fas fa-mobile-alt',
                    labelKey: 'sidebar.module1.devices',
                    requiredModule: 'device',
                    requiredAction: 'viewDevice',
                },
                {
                    path: '/devices/configuration',
                    icon: 'fas fa-cogs',
                    labelKey: 'sidebar.module1.deviceConfiguration',
                    requiredModule: 'devicetype',
                    requiredAction: 'viewDeviceType',
                },
            ],
        },
        {
            id: 2,
            titleKey: 'sidebar.module2.title',
            icon: 'fas fa-certificate',
            color: 'module-2',
            submenu: [
                {
                    path: '/license-management/licenses',
                    icon: 'fas fa-id-card',
                    labelKey: 'sidebar.module2.licenses',
                    requiredModule: 'license',
                    requiredAction: 'viewLicense',
                },
                {
                    path: '/license-management/shared-access-management',
                    icon: 'fas fa-share-alt',
                    labelKey: 'sidebar.module2.sharedAccessManagement',
                    requiredModule: 'license',
                    requiredAction: 'viewLicense',
                    external: true,
                },
                {
                    path: '/license-management/firmware-versions',
                    icon: 'fas fa-microchip',
                    labelKey: 'sidebar.module2.firmwareVersions',
                    requiredModule: 'firmwareversion',
                    requiredAction: 'viewFirmwareVersion',
                },
                {
                    path: '/license-management/units',
                    icon: 'fas fa-building',
                    labelKey: 'sidebar.module2.licenseUnits',
                    requiredModule: 'unit',
                    requiredAction: 'viewUnit',
                },
            ],
        },
        {
            id: 3,
            titleKey: 'sidebar.module3.title',
            icon: 'fas fa-list-alt',
            color: 'module-3',
            submenu: [
                {
                    path: '/catalog/units',
                    icon: 'fas fa-building',
                    labelKey: 'sidebar.module3.units',
                    requiredModule: 'unit',
                    requiredAction: 'viewUnit',
                },
                {
                    path: '/catalog/positions',
                    icon: 'fas fa-user-tie',
                    labelKey: 'sidebar.module3.positions',
                    requiredModule: 'position',
                    requiredAction: 'viewPosition',
                },
                {
                    path: '/catalog/visit-types',
                    icon: 'fas fa-users',
                    labelKey: 'sidebar.module3.visitTypes',
                    requiredModule: 'visittype',
                    requiredAction: 'viewVisitType',
                },
                {
                    path: '/catalog/identity-types',
                    icon: 'fas fa-id-card',
                    labelKey: 'sidebar.module3.identityTypes',
                    requiredModule: 'identitytype',
                    requiredAction: 'viewIdentityType',
                },
                {
                    path: '/catalog/vehicles',
                    icon: 'fas fa-car',
                    labelKey: 'sidebar.module3.vehicles',
                    requiredModule: 'vehicle',
                    requiredAction: 'viewVehicle',
                },
                {
                    path: '/catalog/entry-exit-reasons',
                    icon: 'fas fa-door-open',
                    labelKey: 'sidebar.module3.entryExitReasons',
                    requiredModule: 'entryexitreason',
                    requiredAction: 'viewEntryExitReason',
                },
                {
                    path: '/catalog/verify-modes',
                    icon: 'fas fa-fingerprint',
                    labelKey: 'sidebar.module3.verifyModes',
                    requiredModule: 'verifymode',
                    requiredAction: 'viewVerifyMode',
                },
            ],
        },
        {
            id: 4,
            titleKey: 'sidebar.module4.title',
            icon: 'fas fa-users-cog',
            color: 'module-4',
            submenu: [
                {
                    path: '/users/accounts',
                    icon: 'fas fa-user',
                    labelKey: 'sidebar.module4.accounts',
                    requiredModule: 'user',
                    requiredAction: 'viewUser',
                },
                {
                    path: '/users/roles',
                    icon: 'fas fa-user-tag',
                    labelKey: 'sidebar.module4.roles',
                    requiredModule: 'role',
                    requiredAction: 'viewRole',
                },
                {
                    path: '/users/approval',
                    icon: 'fas fa-user-check',
                    labelKey: 'sidebar.module4.approvalAccounts',
                    requiredModule: 'approvalaccount',
                    requiredAction: 'viewApprovalAccount',
                },
                {
                    path: '/users/account-management',
                    icon: 'fas fa-user-shield',
                    labelKey: 'sidebar.module4.accountManagement',
                    requiredModule: 'user',
                    requiredAction: 'viewUser',
                },
            ],
        },
        {
            id: 5,
            titleKey: 'sidebar.module5.title',
            icon: 'fas fa-database',
            color: 'module-5',
            submenu: [
                {
                    path: '/backup/sync-data',
                    icon: 'fas fa-sync-alt',
                    labelKey: 'sidebar.module5.syncData',
                    requiredModule: 'backup',
                    requiredAction: 'viewBackup',
                },
                {
                    path: '/backup/units',
                    icon: 'fas fa-building',
                    labelKey: 'sidebar.module5.unitsBackup',
                    requiredModule: 'backup',
                    requiredAction: 'viewBackup',
                },
                {
                    path: '/backup/btl86',
                    icon: 'fas fa-cloud-upload-alt',
                    labelKey: 'sidebar.module5.btl86Backup',
                    requiredModule: 'backup',
                    requiredAction: 'viewBackup',
                },
                {
                    path: '/backup/system',
                    icon: 'fas fa-server',
                    labelKey: 'sidebar.module5.systemBackup',
                    requiredModule: 'backup',
                    requiredAction: 'viewBackup',
                },
                {
                    path: '/backup/logs',
                    icon: 'fas fa-history',
                    labelKey: 'sidebar.module5.backupLogs',
                    requiredModule: 'backup',
                    requiredAction: 'viewBackup',
                },
            ],
        },
        {
            id: 6,
            titleKey: 'sidebar.module6.title',
            icon: 'fas fa-chart-line',
            color: 'module-6',
            submenu: [
                {
                    path: '/reports/access',
                    icon: 'fas fa-door-open',
                    labelKey: 'sidebar.module6.accessReport',
                    requiredModule: 'report',
                    requiredAction: 'viewAccessReport',
                },
                {
                    path: '/reports/devices',
                    icon: 'fas fa-server',
                    labelKey: 'sidebar.module6.deviceReport',
                    requiredModule: 'report',
                    requiredAction: 'viewDeviceReport',
                },
                {
                    path: '/reports/violation-statistics',
                    icon: 'fas fa-exclamation-triangle',
                    labelKey: 'sidebar.module6.violationStatistics',
                    requiredModule: 'report',
                    requiredAction: 'viewViolationReport',
                },
                {
                    path: '/reports/database-analysis',
                    icon: 'fas fa-database',
                    labelKey: 'sidebar.module6.databaseAnalysis',
                    requiredModule: 'databaseanalysis',
                    requiredAction: 'viewDatabaseAnalysis',
                },
                {
                    path: '/reports/backup-logs',
                    icon: 'fas fa-history',
                    labelKey: 'sidebar.module5.backupLogs',
                    requiredModule: 'systemlog',
                    requiredAction: 'viewSystemLog',
                },
                {
                    path: '/statistics/vneid',
                    icon: 'fas fa-id-card',
                    labelKey: 'sidebar.module6.vneidStatistics',
                    requiredModule: 'setting',
                    requiredAction: 'viewSetting',
                },
                {
                    path: '/reports/lgsp-integration',
                    icon: 'fas fa-database',
                    labelKey: 'sidebar.module6.lgspIntegration',
                    requiredModule: 'systemlog',
                    requiredAction: 'viewSystemLog',
                },
            ],
        },
        {
            id: 7,
            titleKey: 'sidebar.module7.title',
            icon: 'fas fa-cog',
            color: 'module-7',
            submenu: [
                {
                    path: '/system/settings',
                    icon: 'fas fa-sliders-h',
                    labelKey: 'sidebar.module7.settings',
                    requiredModule: 'setting',
                    requiredAction: 'viewSetting',
                },
                {
                    path: '/system/logs',
                    icon: 'fas fa-file-alt',
                    labelKey: 'sidebar.module7.systemLogs',
                    requiredModule: 'systemlog',
                    requiredAction: 'viewSystemLog',
                },
                {
                    path: '/system/alert-configurations',
                    icon: 'fas fa-bell',
                    labelKey: 'sidebar.module7.alertConfigurations',
                    requiredModule: 'setting',
                    requiredAction: 'viewSetting',
                },
            ],
        },
    ];

    // Auto-expand module based on current route
    useEffect(() => {
        const currentPath = location.pathname;

        // Find which module contains the current route
        for (const module of modules) {
            const hasMatchingRoute = module.submenu.some((item) => currentPath.startsWith(item.path));
            if (hasMatchingRoute) {
                setExpandedModule(module.id);
                break;
            }
        }
    }, [location.pathname]);

    // Filter modules to only show those with at least one visible submenu item
    const visibleModules = modules
        .map((module) => {
            // Filter submenu items based on permissions
            const visibleSubmenu = module.submenu.filter((item) => {
                // If no permission required, show the item
                if (!item.requiredModule || !item.requiredAction) {
                    return true;
                }
                // Check if user has the required permission
                return hasPermission(item.requiredModule, item.requiredAction);
            });

            return {
                ...module,
                submenu: visibleSubmenu,
            };
        })
        .filter((module) => module.submenu.length > 0); // Only show modules with at least one visible submenu

    return (
        <div className={`sidebar-container ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar p-2">
                <h4 className="text-white mb-3">
                    <img src="/logo.png" alt="Logo" style={{ width: '24px', height: '24px', marginRight: '2px' }} />
                    {!isCollapsed && <span>{t('sidebar.systemTitle')}</span>}
                </h4>

                {/* Only show dashboard if user has dashboard permission */}
                {hasPermission('dashboard', 'viewDashboard') && (
                    <Link to="/dashboard" className="dashboard-title">
                        <i className="fas fa-tachometer-alt"></i>
                        {!isCollapsed && <span>{t('sidebar.dashboardTitle')}</span>}
                    </Link>
                )}

                <nav className="nav flex-column">
                    {visibleModules.map((module) => (
                        <div
                            className={`module-item ${isCollapsed && hoveredModule === module.id ? 'module-item-hovered' : ''}`}
                            key={module.id}
                            onMouseEnter={(e) => handleModuleMouseEnter(module.id, e)}
                            onMouseLeave={handleModuleMouseLeave}
                        >
                            <div
                                className={`module-header ${module.color} ${expandedModule === module.id ? 'active' : ''}`}
                                onClick={() => toggleModule(module.id)}
                            >
                                <div className="module-title">
                                    <i className={module.icon}></i>
                                    {!isCollapsed && <span>{t(module.titleKey)}</span>}
                                </div>
                                {!isCollapsed && (
                                    <i className={`fas fa-chevron-right toggle-icon ${expandedModule === module.id ? 'expanded' : ''}`}></i>
                                )}
                            </div>
                            {!isCollapsed && (
                                <div className={`submenu-container ${expandedModule === module.id ? 'expanded' : ''}`}>
                                    {module.submenu.map((item, index) => {
                                        // Check exact match only - each menu item represents a distinct page
                                        const isActive = location.pathname === item.path;

                                        // Handle external links (open in new tab)
                                        if (item.external) {
                                            const handleExternalClick = (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const url = config.app.qlrvWebUrl;
                                                console.log('External link clicked, URL:', url);
                                                if (url) {
                                                    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
                                                    if (!newWindow) {
                                                    }
                                                } else {
                                                }
                                            };

                                            return (
                                                <a
                                                    key={index}
                                                    className={`nav-link small ${isActive ? 'active' : ''}`}
                                                    href={config.app.qlrvWebUrl || '#'}
                                                    onClick={handleExternalClick}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <i className={item.icon}></i> {t(item.labelKey)}
                                                </a>
                                            );
                                        }

                                        return (
                                            <Link key={index} className={`nav-link small ${isActive ? 'active' : ''}`} to={item.path}>
                                                <i className={item.icon}></i> {t(item.labelKey)}
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                            {isCollapsed && hoveredModule === module.id && (
                                <div
                                    className="submenu-popup"
                                    style={{ top: `${popupPosition.top}px` }}
                                    onMouseEnter={handlePopupMouseEnter}
                                    onMouseLeave={handlePopupMouseLeave}
                                >
                                    <div className="submenu-popup-header">{t(module.titleKey)}</div>
                                    {module.submenu.map((item, index) => {
                                        // Check exact match only - each menu item represents a distinct page
                                        const isActive = location.pathname === item.path;

                                        // Handle external links (open in new tab)
                                        if (item.external) {
                                            const handleExternalClick = (e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const url = config.app.qlrvWebUrl;
                                                console.log('External link clicked, URL:', url);
                                                if (url) {
                                                    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
                                                    if (!newWindow) {
                                                    }
                                                } else {
                                                }
                                            };

                                            return (
                                                <a
                                                    key={index}
                                                    className={`submenu-popup-link ${isActive ? 'active' : ''}`}
                                                    href={config.app.qlrvWebUrl || '#'}
                                                    onClick={handleExternalClick}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <i className={item.icon}></i>
                                                    <span>{t(item.labelKey)}</span>
                                                </a>
                                            );
                                        }

                                        return (
                                            <Link key={index} className={`submenu-popup-link ${isActive ? 'active' : ''}`} to={item.path}>
                                                <i className={item.icon}></i>
                                                <span>{t(item.labelKey)}</span>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ))}
                </nav>

                {!isCollapsed && (
                    <a
                        href={config.app.tutorialUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tutorial-link-btn"
                        id="tutorialLink"
                        name="tutorialLink"
                        data-testid="sidebar-link-tutorial"
                        title={t('login.tutorial')}
                    >
                        {t('login.tutorial')}
                    </a>
                )}

                <button
                    className="toggle-sidebar-btn"
                    onClick={toggleSidebar}
                    id="toggleSidebar"
                    name="toggleSidebar"
                    data-testid="sidebar-button-toggle"
                    title={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
                >
                    <i className={`fas ${isCollapsed ? 'fa-indent' : 'fa-outdent'}`}></i>
                </button>
            </div>
        </div>
    );
}

export default Sidebar;
